import { FunctionalComponent, h } from 'preact';

import { configGetCountry } from 'common/helper/config/get-country';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { GoogleRecaptchaTemplate } from 'common/module/google/recaptcha/template';
import { LoaderComponent } from 'common/module/loader/component';
import { UserRegistrationTemplatePropsInterface } from 'common/module/user/registration/template-props.interface';

// tslint:disable-next-line:max-line-length
const authText = i18nTranslate(
  `By clicking 'Create Account' you confirm that you accept Propertyfinder FZ-LLC's {link}. Please view {link} to find out more about how we collect, use and keep your data safe.`
);
// tslint:enable-next-line:max-line-length
const authTextParts = authText.split('{link}');

export const UserRegistrationTemplate: FunctionalComponent<UserRegistrationTemplatePropsInterface> = (props) => (
  <div className='authentication'>
    <div className='authentication__close-button' onClick={props.onClose}>
      {i18nTranslate('Close')}
    </div>
    <div className='authentication__content'>
      <form onSubmit={props.onSubmit}>
        <h2 className='authentication__heading'>{i18nTranslate('New user')}</h2>
        {props.error && <div className='authentication__error--general'>{props.error}</div>}
        <div className='authentication__form'>
          <div className='authentication__name-area'>
            <div className='authentication__input-area--name'>
              <div className='authentication__input-area authentication__input-area--first-name'>
                <div className='authentication__input-label'>{i18nTranslate('First name')}</div>
                <input
                  type='text'
                  className='authentication__input'
                  value={props.fields.first_name}
                  onChange={(e) => props.onChangeInput(e, 'first_name')}
                />
              </div>
              <div className='authentication__input-area authentication__input-area--last-name'>
                <div className='authentication__input-label'>{i18nTranslate('Last name')}</div>
                <input
                  type='text'
                  className='authentication__input'
                  value={props.fields.last_name}
                  onChange={(e) => props.onChangeInput(e, 'last_name')}
                />
              </div>
            </div>
            <div className='authentication__error authentication__error--name'>{props.fieldErrors.userName}</div>
          </div>
          <div className='authentication__input-area'>
            <div className='authentication__input-label'>{i18nTranslate('Enter your email')}</div>
            <input
              type='text'
              className='authentication__input'
              value={props.fields.email}
              onChange={(e) => props.onChangeInput(e, 'email')}
            />
            <div className='authentication__error'>{props.fieldErrors.email}</div>
          </div>
          <div className='authentication__input-area'>
            <div className='authentication__input-label'>{i18nTranslate('Create your password')}</div>
            <input
              type='password'
              className='authentication__input'
              value={props.fields.password}
              onChange={(e) => props.onChangeInput(e, 'password')}
            />
            <div className='authentication__error'>{props.fieldErrors.password}</div>
          </div>

          {props.isAdditionalConditionEnabled && (
            <div className='authentication__policy-check'>
              <div
                className='authentication__checkbox-area authentication__general-condition'
                onClick={props.onClickTermsAndConditions}
              >
                <div
                  className={`authentication__checkbox
                    ${props.fields.termsAndConditions ? 'authentication__checkbox--checked' : ''}`}
                >
                  <span className='svg'>
                    <svg viewBox='0 0 12 9' className='authentication__checkbox-icon'>
                      <use xlinkHref='#svg-check-icon' />
                    </svg>
                  </span>
                </div>
                <div className='authentication__checkbox-text'>
                  {i18nTranslate('I have read and I accept the ')}
                  <a
                    href={configGetCountry().termsUrl}
                    target='_blank'
                    className='authentication__checkbox-link'
                    rel='noreferrer'
                  >
                    {i18nTranslate('general conditions of use ')}
                  </a>
                  {i18nTranslate(', in particular, the mention relating to the protection of personal data')}
                </div>
              </div>
              <div className='authentication__checkbox-area' onClick={props.onClickUseEmail}>
                <div
                  className={`authentication__checkbox
                    ${props.fields.useEmail ? 'authentication__checkbox--checked' : ''}`}
                >
                  <span className='svg'>
                    <svg viewBox='0 0 12 9' className='authentication__checkbox-icon'>
                      <use xlinkHref='#svg-check-icon' />
                    </svg>
                  </span>
                </div>
                <div className='authentication__checkbox-text'>
                  {i18nTranslate('I have read and I accept to receive advertising messages')}
                </div>
              </div>
            </div>
          )}

          {!props.isAdditionalConditionEnabled && (
            <div className='authentication__policy-check'>
              <div className='authentication__checkbox-area authentication__input-area' onClick={props.onClickOptedIn}>
                <div
                  className={`authentication__checkbox
                    ${props.fields.opted_in ? 'authentication__checkbox--checked' : ''}`}
                >
                  <span className='svg'>
                    <svg viewBox='0 0 12 9' className='authentication__checkbox-icon'>
                      <use xlinkHref='#svg-check-icon' />
                    </svg>
                  </span>
                </div>
                <div className='authentication__checkbox-text authentication__checkbox-text--small'>
                  {i18nTranslate(
                    'Send me free guides, market price trends and property recommendations ' +
                      'to help my search. (Don’t worry, you can unsubscribe easily any time.)'
                  )}
                </div>
              </div>
            </div>
          )}

          <div className='authentication__input-area'>
            <GoogleRecaptchaTemplate {...props.captcha} />
            <div className='authentication__error'>{props.fieldErrors.captcha_token}</div>
          </div>

          <div
            className={`authentication__form-submit
          ${
            props.isAdditionalConditionEnabled && (!props.fields.useEmail || !props.fields.termsAndConditions)
              ? 'authentication__form-submit--disabled'
              : ''
          }`}
          >
            <button className='button' type='submit'>
              {i18nTranslate('Create account')}
            </button>
          </div>

          {!props.isAdditionalConditionEnabled && (
            <div className='authentication__policy-check'>
              <div className='authentication__compliance-text'>
                {authTextParts[0]}
                <a
                  href={configGetCountry().termsUrl}
                  target='_blank'
                  className='authentication__compliance-link'
                  rel='noreferrer'
                >
                  {i18nTranslate('Terms and Conditions')}
                </a>
                {authTextParts[1]}
                <a
                  href={configGetCountry().privacyPolicyUrl}
                  target='_blank'
                  className='authentication__compliance-link'
                  rel='noreferrer'
                >
                  {i18nTranslate('our privacy policy')}
                </a>
                {authTextParts[2]}
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
    <div className='authentication__footer'>
      <div className='authentication__footer-txt'>{i18nTranslate('Already registered?')}</div>
      <div className='authentication__footer-link' onClick={props.onClickLogin}>
        {i18nTranslate('Login Here')}
      </div>
    </div>
    <LoaderComponent isEnabled={props.loaderEnabled} isCentered={true} />
  </div>
);
